import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { ConnectionStatus, WebSocketService } from '@freelancer/datastore';
import { TimeUtils } from '@freelancer/time-utils';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { FontColor, TextSize } from '@freelancer/ui/text';
import type { Observable } from 'rxjs';
import { of, Subscription } from 'rxjs';
import { map, mergeMap, startWith } from 'rxjs/operators';

const ONLINE_DISPLAY_DURATION = 1000;

@Component({
  standalone: false,
  selector: 'fl-network-banner',
  template: `
    @if (!(hideBanner$ | flAsync)) {
      <div class="NetworkBanner">
        <fl-icon
          class="mr-xxxsmall"
          [name]="'ui-info'"
          [color]="
            (websocketStatus$ | flAsync) === ConnectionStatus.CLOSED
              ? IconColor.ERROR
              : IconColor.SUCCESS
          "
          [size]="IconSize.XSMALL"
          [useIconFont]="true"
          [fillIconFont]="true"
        ></fl-icon>
        <ng-container [ngSwitch]="websocketStatus$ | flAsync">
          <ng-container *ngSwitchCase="ConnectionStatus.CLOSED">
            @if (hasTimePassed) {
              <div>
                <p
                  class="text-color-inherit text-body-16"
                  i18n="
                    Network banner text to notify the user trying to connect to
                    chat
                  "
                >
                  Connecting to chat...
                </p>
              </div>
            } @else {
              <p
                class="text-color-inherit text-body-16"
                i18n="
                  Network banner text to notify the user will retry connecting
                  to internet
                "
              >
                Unable to connect to chat, retrying&nbsp;
              </p>
              <fl-relative-time
                [date]="date"
                [size]="TextSize.BODY_16"
                [color]="FontColor.INHERIT"
                [strict]="true"
                (timePassed)="showTimePassedText()"
              ></fl-relative-time>
            }
          </ng-container>
          <ng-container *ngSwitchCase="ConnectionStatus.OPEN">
            <p
              class="text-color-inherit text-body-16"
              i18n="Network banner text to notify the user is back online"
            >
              You are back online
            </p>
          </ng-container>
        </ng-container>
      </div>
    }
  `,
  styleUrls: ['./network-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NetworkBannerComponent implements OnInit, OnDestroy {
  private websocket = inject(WebSocketService);
  private timeUtils = inject(TimeUtils);
  private platformId = inject(PLATFORM_ID);

  IconColor = IconColor;
  IconSize = IconSize;
  ConnectionStatus = ConnectionStatus;
  TextSize = TextSize;
  FontColor = FontColor;

  hideBanner$: Observable<boolean>;
  websocketStatus$: Observable<ConnectionStatus>;
  subscriptions = new Subscription();
  hasTimePassed = false;
  date: Date;

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.websocketStatus$ = this.websocket
        .getConnectStatusForUser()
        .pipe(map(bundle => bundle.status));
      this.hideBanner$ = this.websocketStatus$.pipe(
        mergeMap(status => {
          if (status === ConnectionStatus.CLOSED) {
            return of(false);
          }
          return of(true).pipe(this.timeUtils.rxDelay(ONLINE_DISPLAY_DURATION));
        }),
        startWith(true),
      );

      this.subscriptions.add(
        this.websocket.getConnectStatusForUser().subscribe(detail => {
          if (
            detail.status === ConnectionStatus.CLOSED &&
            detail.retryDelayInSeconds > 0
          ) {
            this.date = new Date(
              Date.now() + detail.retryDelayInSeconds * 1000,
            );
          }
          this.hasTimePassed = false;
        }),
      );
    }
  }

  showTimePassedText(): void {
    this.hasTimePassed = true;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
